import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import { useSearchParams } from 'react-router-dom';

import { useTimeTrackingStore } from '../store/time-tracking.store';
import { ViewMode } from '../types/time-tracking';

const VIEW_PARAM = 'view';
const DATE_PARAM = 'date';
const SELECTED_DAY_PARAM = 'selectedDay';

const URL_DATE_FORMAT = 'yyyy-MM-dd';

const VIEW_MODE_VALUES = new Set<string>(Object.values(ViewMode));

const parseViewMode = (raw: string | null): ViewMode | null => {
  if (!raw) return null;
  return VIEW_MODE_VALUES.has(raw) ? (raw as ViewMode) : null;
};

const parseDate = (raw: string | null): Date | null => {
  if (!raw) return null;
  const parsed = parseISO(raw);
  return isValid(parsed) ? parsed : null;
};

const formatDateForUrl = (date: Date): string => format(date, URL_DATE_FORMAT);

interface UseViewModeAndDateResult {
  viewMode: ViewMode;
  selectedDate: Date;
  selectedDay: Date | null;
  setViewMode: (mode: ViewMode) => void;
  setSelectedDate: (date: Date) => void;
  setSelectedDay: (date: Date | null) => void;
  setViewModeAndDate: (mode: ViewMode, date: Date) => void;
  goToToday: () => void;
  navigatePrevious: () => void;
  navigateNext: () => void;
}

/**
 * Drives time-tracking view state from the URL search params. Falls back to the
 * persisted Zustand `viewMode` when the URL is empty so the user lands on their
 * last-used view; the URL becomes canonical after the first interaction.
 *
 * Note: `selectedDate` is intentionally **not** persisted — dates shouldn't
 * survive sessions. We default to today when no URL param is present.
 */
export const useViewModeAndDate = (): UseViewModeAndDateResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const storedViewMode = useTimeTrackingStore(state => state.viewMode);
  const setStoredViewMode = useTimeTrackingStore(state => state.setViewMode);

  const urlViewMode = parseViewMode(searchParams.get(VIEW_PARAM));
  const urlDate = parseDate(searchParams.get(DATE_PARAM));
  const urlSelectedDay = parseDate(searchParams.get(SELECTED_DAY_PARAM));

  const viewMode = urlViewMode ?? storedViewMode;
  // Stabilize the "today" fallback so `selectedDate`'s reference doesn't churn
  // every render — otherwise downstream queryKeys (useSummary, etc.) thrash and
  // can suppress subsequent setSearchParams updates from `setViewMode`.
  const todayAnchorRef = useRef<Date>(new Date());
  const urlDateMs = urlDate ? urlDate.getTime() : null;
  const selectedDate = useMemo(
    () => (urlDateMs !== null ? new Date(urlDateMs) : todayAnchorRef.current),
    [urlDateMs]
  );

  // First-load reconciliation: if URL is missing values, write them in so links are shareable.
  useEffect(() => {
    if (urlViewMode && urlDate) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (!urlViewMode) next.set(VIEW_PARAM, viewMode);
        if (!urlDate) next.set(DATE_PARAM, formatDateForUrl(selectedDate));
        return next;
      },
      { replace: true }
    );
    // Intentional: run once on mount; subsequent param changes flow through setters below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateParams = useCallback(
    (mutate: (next: URLSearchParams) => void, replace = false) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      setStoredViewMode(mode);
      updateParams(next => {
        next.set(VIEW_PARAM, mode);
        // Clear the selectedDay (month-view's right panel pointer) on view switches.
        next.delete(SELECTED_DAY_PARAM);
      });
    },
    [setStoredViewMode, updateParams]
  );

  const setSelectedDate = useCallback(
    (date: Date) => {
      updateParams(next => {
        next.set(DATE_PARAM, formatDateForUrl(date));
        // Clear the selectedDay anchor when the user picks a new date — month-view
        // recomputes its default highlight on the new month.
        next.delete(SELECTED_DAY_PARAM);
      });
    },
    [updateParams]
  );

  const setSelectedDay = useCallback(
    (date: Date | null) => {
      updateParams(next => {
        if (date) next.set(SELECTED_DAY_PARAM, formatDateForUrl(date));
        else next.delete(SELECTED_DAY_PARAM);
      });
    },
    [updateParams]
  );

  // Atomic view+date switch. Doing this as two separate setters
  // (`setViewMode` then `setSelectedDate`) loses one of the URL writes —
  // each `setSearchParams` reads the committed render state, so the
  // second call overwrites the first within the same tick.
  const setViewModeAndDate = useCallback(
    (mode: ViewMode, date: Date) => {
      setStoredViewMode(mode);
      updateParams(next => {
        next.set(VIEW_PARAM, mode);
        next.set(DATE_PARAM, formatDateForUrl(date));
        next.delete(SELECTED_DAY_PARAM);
      });
    },
    [setStoredViewMode, updateParams]
  );

  const goToToday = useCallback(() => {
    setSelectedDate(new Date());
  }, [setSelectedDate]);

  const navigatePrevious = useCallback(() => {
    const base = selectedDate;
    if (viewMode === ViewMode.Day) setSelectedDate(subDays(base, 1));
    else if (viewMode === ViewMode.Week) setSelectedDate(subDays(base, 7));
    else setSelectedDate(subMonths(base, 1));
  }, [selectedDate, viewMode, setSelectedDate]);

  const navigateNext = useCallback(() => {
    const base = selectedDate;
    if (viewMode === ViewMode.Day) setSelectedDate(addDays(base, 1));
    else if (viewMode === ViewMode.Week) setSelectedDate(addDays(base, 7));
    else setSelectedDate(addMonths(base, 1));
  }, [selectedDate, viewMode, setSelectedDate]);

  return {
    viewMode,
    selectedDate,
    selectedDay: urlSelectedDay,
    setViewMode,
    setSelectedDate,
    setSelectedDay,
    setViewModeAndDate,
    goToToday,
    navigatePrevious,
    navigateNext,
  };
};

/**
 * Computes the date range (start/end ISO) for a given viewMode + anchor date.
 * Shared by `useSummary`, `useTimeTracks`, `useWeekOverview`, `useMonthOverview`.
 */
export const getDateRangeForView = (date: Date, viewMode: ViewMode): { start: Date; end: Date } => {
  if (viewMode === ViewMode.Day) {
    return { start: date, end: date };
  }
  if (viewMode === ViewMode.Week) {
    return {
      start: startOfWeek(date, { weekStartsOn: 1 }),
      end: endOfWeek(date, { weekStartsOn: 1 }),
    };
  }
  return { start: startOfMonth(date), end: endOfMonth(date) };
};
